import { useEffect, useState, type FormEvent } from "react";

import { ArrowLeft } from "phosphor-react";

import { CloseButton } from "../../close-button";
import { Loading } from "../../loading";
import { ImagePreview } from "../image-preview";
import { UploadButton } from "../upload-button";

import { feedbackTypes, type FeedbackType } from "..";
import { getFeedgetInstance } from "../../../lib/api";

interface FeedbackContentStepProps {
  feedbackType: FeedbackType;
  onFeedbackRestartRequested: () => void;
  onFeedbackSent: () => void;
}

export function FeedbackContentStep({
  feedbackType,
  onFeedbackRestartRequested,
  onFeedbackSent,
}: FeedbackContentStepProps) {
  const feedbackTypeInfo = feedbackTypes[feedbackType];

  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    setUploadedImage(null);
    setComment("");
    setIsSendingFeedback(false);
  }, [feedbackType]);

  const handleSubmitFeedback = async (e: FormEvent) => {
    e.preventDefault();
    setIsSendingFeedback(true);

    try {
      const API = getFeedgetInstance();

      await API.submitFeedback({
        type: feedbackType,
        comment,
        screenshot: uploadedImage || undefined,
      });

      onFeedbackSent();
    } catch (err: any) {
      console.error("Erro ao enviar feedback:", err);
    } finally {
      setIsSendingFeedback(false);
    }
  };

  return (
    <>
      <header className="flex items-center justify-between w-full px-2">
        <button
          type="button"
          title="Voltar"
          aria-label="Voltar"
          onClick={onFeedbackRestartRequested}
          className="text-zinc-400 hover:text-zinc-100 cursor-pointer transition-colors"
        >
          <ArrowLeft weight="bold" className="w-4 h-4" />
        </button>

        <span className="text-lg font-medium flex items-center gap-2 text-zinc-100">
          <img
            src={feedbackTypeInfo.image.source}
            alt={feedbackTypeInfo.image.alt}
            className="w-6 h-6"
          />
          {feedbackTypeInfo.title}
        </span>

        <CloseButton />
      </header>

      <form onSubmit={handleSubmitFeedback} className="my-4 w-full space-y-4 animate-fade-in">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Conte com detalhes o que está acontecendo..."
          className="md:min-w-[304px] w-full min-h-[112px] text-sm placeholder-zinc-500 text-zinc-100 border-zinc-600 bg-zinc-950/30 backdrop-blur-sm rounded-md focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none resize-none scrollbar-thumb-zinc-700 scrollbar-track-transparent scrollbar-thin p-3 transition-all duration-200 ease-in-out hover:border-zinc-500"
          minLength={10}
          maxLength={1000}
          required
        />

        <footer className="flex gap-2 mt-2 relative">
          <div className="relative">
            <UploadButton
              image={uploadedImage}
              onImageSelected={setUploadedImage}
            />
            {uploadedImage && (
              <ImagePreview
                image={uploadedImage}
                onRemove={() => setUploadedImage(null)}
                type="upload"
              />
            )}
          </div>

          <button
            type="submit"
            title="Enviar feedback"
            aria-labelledby="Enviar feedback"
            disabled={
              comment.length < 10 || comment.length > 1000 || isSendingFeedback
            }
            className="p-2 bg-brand-500 rounded-md border-transparent flex flex-1 justify-center items-center text-sm hover:bg-brand-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-brand-500 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:hover:bg-brand-500 cursor-pointer disabled:cursor-not-allowed font-medium text-white shadow-lg hover:shadow-brand-500/25 active:scale-[0.98]"
          >
            {isSendingFeedback ? <Loading /> : "Enviar feedback"}
          </button>
        </footer>

        <div className="text-xs text-zinc-500 mt-1 text-right transition-colors duration-300" style={{ color: comment.length > 900 ? '#ef4444' : undefined }}>
          {comment.length}/1000
        </div>
      </form>
    </>
  );
}
